import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { GlassButton } from 'dashboard/components/common';

/**
 * Asks about hidden sections before a page goes live.
 *
 * Sections start hidden, which is right while one is half-built — but it puts
 * a trap at the end: press Publish, and the section you spent ten minutes on
 * is not on the page, with nothing anywhere saying why. This is where that
 * gets said, at the only moment it matters.
 *
 * Ticked by default, because someone who just built a section and pressed
 * Publish almost certainly means to publish it. The list is there for the
 * exception — the one still being worked on — not to make the common case
 * into a chore.
 */
export default function PublishGuardDialog({ open, sections = [], onCancel, onConfirm }) {
  const [chosen, setChosen] = useState({});

  useEffect(() => {
    if (!open) return;
    setChosen(Object.fromEntries(sections.map((s) => [s._id, true])));
  }, [open, sections]);

  const toShow = sections.filter((s) => chosen[s._id]);
  const leaving = sections.length - toShow.length;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth='xs'>
      <DialogTitle>
        {sections.length === 1
          ? 'One section is still hidden'
          : `${sections.length} sections are still hidden`}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' color='text.secondary' mb='12px'>
          Hidden sections do not appear on your live site. Tick the ones to
          show when this page publishes.
        </Typography>
        <Stack>
          {sections.map((s) => (
            <FormControlLabel
              key={s._id}
              control={
                <Checkbox
                  size='small'
                  checked={!!chosen[s._id]}
                  onChange={(e) => setChosen((c) => ({ ...c, [s._id]: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant='body2'>{s.name || s.type}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {s.region} · {s.type}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: '8px' }}>
        <GlassButton onClick={onCancel}>Cancel</GlassButton>
        <GlassButton variant='contained' onClick={() => onConfirm(toShow.map((s) => s._id))}>
          {leaving > 0
            ? `Publish, leaving ${leaving} hidden`
            : 'Show them and publish'}
        </GlassButton>
      </DialogActions>
    </Dialog>
  );
}
